import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
  QueryConstraint,
  limit,
  startAfter,
  DocumentData,
} from 'firebase/firestore';
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { db, auth } from '@/lib/firebase';
import { apiFetch, withToken } from '@/lib/api';

export interface WorkflowSummary {
  id: string;
  name: string;
  description: string;
  owner: string;
  ownerName: string;
  categories: string[];
  status: 'draft' | 'published';
  createdAt: string;
  updatedAt: string;
  nodeCount: number;
  outputNodeCount: number;
  enriched: boolean;
  hidden?: boolean;
}

interface AnalyzedInput {
  nodeId: string;
  nodeType: string;
  nodeTitle?: string;
  inputName: string;
  currentValue: string | number | boolean;
  inferredType: string;
}

export interface AnalyzedOutput {
  nodeId: string;
  nodeType: string;
  outputKind: 'image' | 'video' | 'preview' | 'unknown';
}

export interface WorkflowAnalysis {
  nodeCount: number;
  inputNodes: AnalyzedInput[];
  outputNodes: AnalyzedOutput[];
}

export interface InputField {
  id: string;
  nodeId: string;
  inputName: string;
  label: string;
  widget: 'text' | 'textarea' | 'number' | 'slider' | 'toggle' | 'seed' | 'select' | 'file-image' | 'file-video' | 'file-audio';
  defaultValue: string | number | boolean;
  visible: boolean;
  required: boolean;
  order: number;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  // When a manager constrains a field to a list of allowed values, the widget
  // becomes 'select' and baseWidget preserves the original widget so the change
  // can be reverted.
  baseWidget?: InputField['widget'];
  tooltip?: string;
  /** Customizable description shown to users (frontend-only, not sent to backend execution). */
  description?: string;
  /** All options provided by ComfyUI enrichment (before manager curation). */
  enrichedOptions?: string[];
  /** Original max value from ComfyUI enrichment (slider shows this as upper bound). */
  enrichedMax?: number;
}

export interface WorkflowDetail {
  id: string;
  name: string;
  rawWorkflow: Record<string, unknown>;
  analysis: WorkflowAnalysis;
  inputMask: InputField[];
  categories?: string[];
  enriched: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExecutionOutput {
  nodeId: string;
  filename: string;
  url: string;
  type: string;
  subfolder?: string;
  hidden?: boolean;
}

export interface ExecutionStatus {
  status: 'queued' | 'running' | 'localizing' | 'completed' | 'error' | 'cancelled' | 'unknown';
  workflowId?: string;
  outputs?: ExecutionOutput[];
  error?: string;
}

export interface ResolvedInput {
  value: string | number | boolean;
  nodeType: string;
  nodeTitle?: string;
}

export interface ExecutionRecord {
  id: string;
  workflowId: string;
  workflowName: string;
  status: string;
  error?: string;
  errorDetails?: string;
  inputParams?: Record<string, unknown>;
  inputFileNames?: Record<string, string>;
  inputMeta?: Record<string, { nodeName: string; label: string }>;
  /** Complete snapshot of every primitive input in the executed graph (newer runs only). */
  resolvedInputs?: Record<string, ResolvedInput>;
  /** Number of snapshot entries omitted due to the size budget (rare, huge graphs). */
  resolvedInputsOmitted?: number;
  executedBy?: { uid: string; name: string };
  outputs: ExecutionOutput[];
  createdAt: string;
  completedAt?: string;
}

export type HiddenFilter = 'active' | 'all' | 'only';

interface UseRealtimeWorkflowsReturn {
  workflows: WorkflowSummary[];
  executions: ExecutionRecord[];
  loading: boolean;
  error: string | null;
  hasMoreExecutions: boolean;
  executionsPage: number;
  uploadWorkflow: (name: string, workflow: Record<string, unknown>) => Promise<{ id: string }>;
  getWorkflow: (id: string) => Promise<WorkflowDetail>;
  deleteWorkflow: (id: string) => Promise<void>;
  enrichWorkflow: (id: string) => Promise<WorkflowDetail>;
  updateWorkflow: (id: string, data: Record<string, unknown>) => Promise<WorkflowDetail>;
  executeWorkflow: (
    id: string,
    params: Record<string, unknown>,
    filePaths: Record<string, string>,
  ) => Promise<{ promptId: string }>;
  cancelExecution: (promptId: string) => Promise<void>;
  getOutputUrl: (promptId: string, filename: string) => string;
  getInputUrl: (promptId: string, filename: string) => string;
  refreshExecutionOutputs: (promptId: string) => Promise<void>;
  fetchExecution: (promptId: string) => Promise<ExecutionRecord | null>;
  setStatusFilter: (status?: 'draft' | 'published') => void;
  // Server-side status filter for the executions query (e.g. 'completed' for My
  // Outputs). Pass undefined to load executions of any status.
  setExecutionStatusFilter: (status?: ExecutionRecord['status']) => void;
  setAccessOnly: (value: boolean) => void;
  nextExecutionsPage: () => void;
  previousExecutionsPage: () => void;
  resetExecutionsPage: () => void;
  // Opt into accumulate mode: advancing a page APPENDS older executions instead
  // of swapping the window. Used by gallery-style views ("Load more"); leave off
  // for classic page-at-a-time views (History).
  setAccumulateExecutions: (value: boolean) => void;
  loadingMoreExecutions: boolean;
  refreshWorkflows: () => Promise<void>;
  hiddenFilter: HiddenFilter;
  setHiddenFilter: (filter: HiddenFilter) => void;
  toggleHideWorkflow: (id: string, hidden: boolean) => Promise<void>;
  toggleHideOutput: (promptId: string, filename: string, hidden: boolean) => Promise<void>;
  filteringWorkflows: boolean;
}

const EXECUTIONS_PER_PAGE = 20;

export const useRealtimeWorkflows = (): UseRealtimeWorkflowsReturn => {
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [executions, setExecutions] = useState<ExecutionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | undefined>(undefined);
  const [accessOnly, setAccessOnly] = useState(false);
  const [hiddenFilter, setHiddenFilter] = useState<HiddenFilter>('active');
  const [filteringWorkflows, setFilteringWorkflows] = useState(false);
  // Guards against out-of-order fetch responses clobbering newer results.
  const fetchSeqRef = useRef(0);
  // Track if this is the initial load or a filter change
  const isInitialLoadRef = useRef(true);
  const [executionsPage, setExecutionsPage] = useState(0);
  const [lastExecutionDocs, setLastExecutionDocs] = useState<DocumentData[]>([]);
  const [hasMoreExecutions, setHasMoreExecutions] = useState(false);
  const [accumulateExecutions, setAccumulateExecutions] = useState(false);
  // Accumulated pages (accumulate mode only): index i holds the docs for page i,
  // so the combined list grows as pages load instead of swapping the window.
  // Read only via the setter's `prev` callback, so the value binding is omitted.
  const [, setExecutionPages] = useState<ExecutionRecord[][]>([]);
  const [loadingMoreExecutions, setLoadingMoreExecutions] = useState(false);
  const accumulateRef = useRef(accumulateExecutions);
  accumulateRef.current = accumulateExecutions;
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // Optional server-side status filter for the executions query. Pages that only
  // care about completed runs (e.g. My Outputs) set this so error/queued runs
  // don't consume the page budget and hide real outputs further back in time.
  const [executionStatusFilter, setExecutionStatusFilter] = useState<ExecutionRecord['status'] | undefined>(undefined);

  // Fetch workflows from backend API (properly handles group-based access control)
  // Note: We use the backend API instead of Firestore real-time listener because
  // Firestore Security Rules cannot validate group membership (cannot query groups collection).
  // The backend's hasWorkflowAccess() function properly checks group membership.
  const fetchWorkflows = useCallback(
    async (options?: { silent?: boolean; filtering?: boolean }) => {
      // Silent refreshes skip the global loading flag so the page shows an inline
      // spinner (where the list is) instead of replacing the whole page.
      // Filtering uses a separate flag to show an overlay spinner instead of full-page loading.
      if (options?.filtering) {
        setFilteringWorkflows(true);
      } else if (!options?.silent) {
        setLoading(true);
      }
      setError(null);

      const seq = ++fetchSeqRef.current;
      try {
        // Build query string based on status + access + hidden filters
        const params = new URLSearchParams();
        if (statusFilter) params.set('status', statusFilter);
        if (accessOnly) params.set('accessOnly', 'true');
        if (hiddenFilter === 'all') {
          params.set('hidden', 'all');
        } else if (hiddenFilter === 'only') {
          params.set('hidden', 'only');
        }
        const queryParams = params.toString() ? `?${params}` : '';
        const res = await apiFetch(`/workflows${queryParams}`);

        if (!res.ok) {
          throw new Error(`Failed to fetch workflows: ${res.status}`);
        }

        const data = await res.json();

        // A newer fetch started while this one was in flight — discard this result.
        if (seq !== fetchSeqRef.current) return;

        const workflowList = (data.workflows || []).map((wf: Record<string, unknown>) => ({
          id: wf.id as string,
          name: (wf.name as string) || '',
          description: (wf.description as string) || '',
          owner: (wf.owner as string) || '',
          ownerName: (wf.ownerName as string) || (wf.owner as string) || '',
          categories: Array.isArray(wf.categories) ? (wf.categories as string[]) : [],
          status: (wf.status as 'draft' | 'published') || 'draft',
          createdAt: (wf.createdAt as string) || '',
          updatedAt: (wf.updatedAt as string) || '',
          nodeCount: (wf.nodeCount as number) || 0,
          outputNodeCount: (wf.outputNodeCount as number) || 0,
          enriched: (wf.enriched as boolean) || false,
          hidden: (wf.hidden as boolean) || false,
        })) as WorkflowSummary[];

        setWorkflows(workflowList);
        setLoading(false);
        setFilteringWorkflows(false);
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        console.error('Failed to fetch workflows:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch workflows');
        setLoading(false);
        setFilteringWorkflows(false);
      }
    },
    [statusFilter, accessOnly, hiddenFilter],
  );

  useEffect(() => {
    // On initial load, use full-page loading. On filter changes, use overlay spinner.
    const isInitial = isInitialLoadRef.current;
    if (isInitial) {
      isInitialLoadRef.current = false;
      fetchWorkflows();
    } else {
      fetchWorkflows({ filtering: true });
    }
  }, [fetchWorkflows]);

  // Auth check - runs once to verify roles claim
  useEffect(() => {
    let isSubscribed = true;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!isSubscribed) return;

      if (!user) {
        setCurrentUserId(null);
        setIsAuthReady(false);
        setExecutions([]);
        return;
      }

      // Wait for custom claims to be set by polling the token
      // Check for either 'app_roles' (new) or 'roles' (legacy) claim
      const maxAttempts = 5; // Reduced from 15 to fail faster
      let attempt = 0;
      let tokenResult = await getIdTokenResult(user);

      while (attempt < maxAttempts && !tokenResult.claims.app_roles && !tokenResult.claims.roles) {
        attempt++;
        const delay = Math.min(500 * Math.pow(1.3, attempt - 1), 2000); // Max 2s instead of 3s
        await new Promise((resolve) => setTimeout(resolve, delay));

        if (!isSubscribed) return;

        try {
          tokenResult = await getIdTokenResult(user, true);
        } catch (err) {
          console.error('[Executions] Failed to refresh token:', err);
          break; // Stop trying on error
        }
      }

      if (!isSubscribed) return;

      // Allow auth to proceed even if claims aren't ready yet
      // Firestore rules will handle authorization
      if (!tokenResult.claims.app_roles && !tokenResult.claims.roles) {
        console.warn('[Executions] Custom claims not available after max attempts - proceeding anyway');
      }

      setCurrentUserId(user.uid);
      setIsAuthReady(true);
    });

    return () => {
      isSubscribed = false;
      unsubscribeAuth();
    };
  }, []);

  // Use ref to avoid dependency cycle with lastExecutionDocs
  const lastExecutionDocsRef = useRef<DocumentData[]>([]);
  lastExecutionDocsRef.current = lastExecutionDocs;

  // Executions listener - runs when auth is ready or page changes
  useEffect(() => {
    if (!isAuthReady || !currentUserId) return;

    const constraints: QueryConstraint[] = [
      where('executedBy.uid', '==', currentUserId),
      // Optional server-side status filter — keeps error/queued runs from
      // consuming the page budget on completed-only views (e.g. My Outputs).
      ...(executionStatusFilter ? [where('status', '==', executionStatusFilter)] : []),
      orderBy('createdAt', 'desc'),
      limit(EXECUTIONS_PER_PAGE + 1),
    ];

    // Use ref to get current value without adding to dependencies
    if (executionsPage > 0 && lastExecutionDocsRef.current[executionsPage - 1]) {
      constraints.push(startAfter(lastExecutionDocsRef.current[executionsPage - 1]));
    }

    const q = query(collection(db, 'executions'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs;
        const hasMore = docs.length > EXECUTIONS_PER_PAGE;
        setHasMoreExecutions(hasMore);

        const pageLastDoc = docs[EXECUTIONS_PER_PAGE - 1] || docs[docs.length - 1];
        if (pageLastDoc) {
          setLastExecutionDocs((prev) => {
            const updated = [...prev];
            updated[executionsPage] = pageLastDoc;
            return updated;
          });
        }

        const executionList = docs.slice(0, EXECUTIONS_PER_PAGE).map((doc) => {
          const data = doc.data();

          const outputs = (data.outputs || []).map((o: ExecutionOutput) =>
            typeof o.url === 'string' && o.url.startsWith('/') ? { ...o, url: withToken(o.url) } : o,
          );

          return {
            id: doc.id,
            workflowId: data.workflowId || '',
            workflowName: data.workflowName || '',
            status: data.status || 'unknown',
            error: data.error,
            errorDetails: data.errorDetails,
            inputParams: data.inputParams,
            inputFileNames: data.inputFileNames,
            inputMeta: data.inputMeta,
            resolvedInputs: data.resolvedInputs,
            resolvedInputsOmitted: data.resolvedInputsOmitted,
            executedBy: data.executedBy,
            outputs,
            createdAt: data.createdAt || '',
            completedAt: data.completedAt,
          } as ExecutionRecord;
        });

        setLoadingMoreExecutions(false);
        if (accumulateRef.current) {
          // Accumulate mode: store this page by index and expose the combined
          // list, so "Load more" grows the gallery instead of swapping windows.
          // The live listener follows the current page; earlier pages are
          // retained as the snapshots last seen.
          setExecutionPages((prev) => {
            const updated = [...prev];
            updated[executionsPage] = executionList;
            const combined = updated.flat().filter(Boolean);
            setExecutions(combined);
            return updated;
          });
        } else {
          setExecutions(executionList);
        }
      },
      (err) => {
        console.error('Executions listener error:', {
          code: err.code,
          message: err.message,
          name: err.name,
        });
      },
    );

    return () => unsubscribe();
  }, [isAuthReady, currentUserId, executionsPage, executionStatusFilter]);

  // Changing the status filter invalidates the page cursors (they point at docs
  // from the previous query), so reset pagination back to the first page.
  useEffect(() => {
    setExecutionsPage(0);
    setLastExecutionDocs([]);
    setExecutionPages([]);
  }, [executionStatusFilter]);

  const uploadWorkflow = useCallback(async (name: string, workflow: Record<string, unknown>): Promise<{ id: string }> => {
    setError(null);
    try {
      const res = await apiFetch('/workflows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, workflow }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || `Upload failed: ${res.status}`);
      }
      const result = await res.json();
      // No need to manually refresh - real-time listener will update
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, []);

  const getWorkflow = useCallback(async (id: string): Promise<WorkflowDetail> => {
    const res = await apiFetch(`/workflows/${id}`);
    if (!res.ok) throw new Error(`Failed to fetch workflow: ${res.status}`);
    return res.json();
  }, []);

  const deleteWorkflow = useCallback(async (id: string): Promise<void> => {
    setError(null);
    try {
      const res = await apiFetch(`/workflows/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`Failed to delete: ${res.status}`);
      // No need to manually refresh - real-time listener will update
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, []);

  const toggleHideWorkflow = useCallback(
    async (id: string, hidden: boolean): Promise<void> => {
      setError(null);
      try {
        const res = await apiFetch(`/workflows/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ hidden }),
        });
        if (!res.ok) throw new Error(`Failed to ${hidden ? 'archive' : 'unarchive'}: ${res.status}`);
        // Trigger re-fetch to update the list
        await fetchWorkflows({ filtering: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        throw err;
      }
    },
    [fetchWorkflows],
  );

  const enrichWorkflow = useCallback(async (id: string): Promise<WorkflowDetail> => {
    setError(null);
    try {
      const res = await apiFetch(`/workflows/${id}/enrich`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`Enrichment failed: ${res.status}`);
      return res.json();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  }, []);

  const updateWorkflow = useCallback(async (id: string, data: Record<string, unknown>): Promise<WorkflowDetail> => {
    const res = await apiFetch(`/workflows/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    return res.json();
  }, []);

  const executeWorkflow = useCallback(
    async (id: string, params: Record<string, unknown>, filePaths: Record<string, string>): Promise<{ promptId: string }> => {
      const res = await apiFetch(`/workflows/${id}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ params, filePaths }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || `Execution failed: ${res.status}`);
      }

      return res.json();
    },
    [],
  );

  const cancelExecution = useCallback(async (promptId: string): Promise<void> => {
    const res = await apiFetch(`/executions/${promptId}/cancel`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error(`Cancel failed: ${res.status}`);
  }, []);

  const getOutputUrl = useCallback((promptId: string, filename: string): string => {
    return withToken(`/api/v1/executions/${promptId}/output/${encodeURIComponent(filename)}`);
  }, []);

  const getInputUrl = useCallback((promptId: string, filename: string): string => {
    return withToken(`/api/v1/executions/${promptId}/input/${encodeURIComponent(filename)}`);
  }, []);

  // Hit the status endpoint so the backend re-signs any expired output URLs and
  // persists them to Firestore; the realtime listener then delivers fresh URLs.
  const refreshExecutionOutputs = useCallback(async (promptId: string): Promise<void> => {
    try {
      await apiFetch(`/executions/${promptId}`);
    } catch {
      // Non-fatal — the listener keeps whatever URLs it already has.
    }
  }, []);

  const fetchExecution = useCallback(
    async (promptId: string): Promise<ExecutionRecord | null> => {
      return executions.find((e) => e.id === promptId) || null;
    },
    [executions],
  );

  const nextExecutionsPage = useCallback(() => {
    if (hasMoreExecutions) {
      // In accumulate mode, show a "loading more" state until the next page's
      // snapshot arrives and is appended.
      if (accumulateRef.current) setLoadingMoreExecutions(true);
      setExecutionsPage((prev) => prev + 1);
    }
  }, [hasMoreExecutions]);

  const previousExecutionsPage = useCallback(() => {
    if (executionsPage > 0) {
      setExecutionsPage((prev) => prev - 1);
    }
  }, [executionsPage]);

  const resetExecutionsPage = useCallback(() => {
    setExecutionsPage(0);
    setLastExecutionDocs([]);
    setExecutionPages([]);
  }, []);

  const toggleHideOutput = useCallback(async (promptId: string, filename: string, hidden: boolean): Promise<void> => {
    const res = await apiFetch(`/executions/${promptId}/output/${encodeURIComponent(filename)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hidden }),
    });
    if (!res.ok) throw new Error(`Failed to toggle hide: ${res.status}`);
  }, []);

  return {
    workflows,
    executions,
    loading,
    error,
    hasMoreExecutions,
    executionsPage,
    uploadWorkflow,
    getWorkflow,
    deleteWorkflow,
    enrichWorkflow,
    updateWorkflow,
    executeWorkflow,
    cancelExecution,
    getOutputUrl,
    getInputUrl,
    refreshExecutionOutputs,
    fetchExecution,
    setStatusFilter,
    setExecutionStatusFilter,
    setAccessOnly,
    nextExecutionsPage,
    previousExecutionsPage,
    resetExecutionsPage,
    setAccumulateExecutions,
    loadingMoreExecutions,
    refreshWorkflows: () => fetchWorkflows({ silent: true }),
    hiddenFilter,
    setHiddenFilter,
    toggleHideWorkflow,
    toggleHideOutput,
    filteringWorkflows,
  };
};
